import { localCalendar, notificationPlan, TIMER_NOTIFICATION_ID, type PlanItem } from '@hmawiw/shared';
import { currentLocale, translate } from '../i18n';
import { openCardAnywhere } from '../lib/openCard';
import type { router as Router } from '../routes';
import { useCards } from '../store/cards';
import { useSession } from '../store/session';
import { useSettings } from '../store/settings';
import { useTimer } from '../store/timer';
import { useUi } from '../store/ui';
import { Notifier } from './notifier';
import { apiBase } from './platform';

/** Alarms live on their own channel: a ringing sound, vibration and heads-up priority; the user can tune it in Android's settings. */
export const ALARM_CHANNEL = 'alarms';
export const ALARM_SOUND = 'alarm.wav';
/** Today's cards sit on a quiet channel: no sound, no heads-up, just the shade. */
export const TODAY_CHANNEL = 'today';
/** Set once the notifications scheduled by builds before the notifier are gone. */
const MIGRATED_KEY = 'hmawiw-notifier';

export async function ensureAlarmChannel(): Promise<void> {
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    const locale = currentLocale();
    await LocalNotifications.createChannel({
      id: ALARM_CHANNEL,
      name: translate(locale, 'notif.alarmChannel'),
      description: translate(locale, 'notif.alarmChannelHint'),
      importance: 5,
      visibility: 1,
      sound: ALARM_SOUND,
      vibration: true,
      lights: true,
    });
    await LocalNotifications.createChannel({
      id: TODAY_CHANNEL,
      name: translate(locale, 'notif.todayChannel'),
      description: translate(locale, 'notif.todayChannelHint'),
      importance: 2,
      visibility: 0,
      vibration: false,
      lights: false,
    });
  } catch (e) {
    console.warn('notifications: channels failed', e);
  }
}

/** What this phone should hold now, derived from the cards on the device; the server derives the same while the app is closed. */
export function currentPlan(now = Date.now()): PlanItem[] {
  const s = useSettings.getState();
  const session = useSession.getState();
  return notificationPlan({
    cards: Object.values(useCards.getState().cards),
    boards: session.boards,
    now,
    cal: localCalendar,
    lang: currentLocale(),
    labels: {
      columnCount: s.columnCount,
      columnLabels: s.columnLabels,
      bandLabels: s.bandLabels,
      bandPreset: s.bandPreset,
      defaults: { columnLabels: session.defaults.columnLabels, bandLabels: session.defaults.bandLabels },
    },
    checkinTime: s.checkinTime,
    todayShade: s.todayShade,
  });
}

/** Builds before the notifier scheduled through the Capacitor plugin: those go once, or they would ring twice. */
async function migrateOnce(): Promise<void> {
  try {
    if (localStorage.getItem(MIGRATED_KEY)) return;
  } catch {
    return;
  }
  const { LocalNotifications } = await import('@capacitor/local-notifications');
  const pending = (await LocalNotifications.getPending()).notifications;
  if (pending.length) await LocalNotifications.cancel({ notifications: pending.map((p) => ({ id: p.id })) });
  await LocalNotifications.removeAllDeliveredNotifications();
  try {
    localStorage.setItem(MIGRATED_KEY, '1');
  } catch {
    // tried again next time, which only cancels nothing
  }
}

async function permitted(): Promise<boolean> {
  const { LocalNotifications } = await import('@capacitor/local-notifications');
  let permission = await LocalNotifications.checkPermissions();
  if (permission.display !== 'granted') permission = await LocalNotifications.requestPermissions();
  return permission.display === 'granted';
}

let reconciling = false;
let again = false;

/** Idempotent: the plan is derived from synced data and the notifier applies only the difference. */
export async function reconcileNotifications(): Promise<void> {
  if (reconciling) {
    again = true;
    return;
  }
  reconciling = true;
  try {
    await migrateOnce();
    const now = Date.now();
    const items = currentPlan(now);
    const running = useTimer.getState().running;
    const timerCard = running && running.endsAt > now ? useCards.getState().cards[running.cardId] : undefined;
    if ((items.length || timerCard) && !(await permitted())) return;
    await Notifier.apply({ items });
    if (running && timerCard) {
      const locale = currentLocale();
      await Notifier.setTimer({
        item: { id: TIMER_NOTIFICATION_ID, kind: 'timer', title: translate(locale, 'notif.timerTitle'), body: timerCard.title, cardId: timerCard.id, at: running.endsAt, ring: useSettings.getState().timerRing },
      });
    } else {
      await Notifier.setTimer({});
    }
  } catch (e) {
    console.warn('notifications: reconcile failed', e);
  } finally {
    reconciling = false;
    if (again) {
      again = false;
      void reconcileNotifications();
    }
  }
}

/** Hands the background job what it needs to ask the server while the app is closed. */
export async function configureNotifier(): Promise<void> {
  const { status, token } = useSession.getState();
  const server = apiBase();
  if (status !== 'authed' || !token || !server) return;
  const locale = currentLocale();
  try {
    await Notifier.configure({ server, token, lang: locale, today: useSettings.getState().todayShade, done: translate(locale, 'notif.todayDone') });
  } catch (e) {
    console.warn('notifications: configure failed', e);
  }
}

export async function holdNotifier(on: boolean): Promise<void> {
  try {
    await Notifier.hold({ on });
  } catch (e) {
    console.warn('notifications: hold failed', e);
  }
}

export async function clearNotifier(): Promise<void> {
  try {
    await Notifier.clear();
  } catch (e) {
    console.warn('notifications: clear failed', e);
  }
}

/** Done from the shade: the app opens, the card is done (or logged, when it comes back), and the toast can undo it. */
export function completeFromShade(id: string): void {
  const run = (): boolean => {
    if (!useCards.getState().hydrated) return false;
    const card = useCards.getState().cards[id];
    if (!card || card.status !== 'open' || card.deletedAt !== null) return true;
    const locale = currentLocale();
    const { showToast } = useUi.getState();
    if (card.repeat) {
      void useCards.getState().touch(id);
      showToast(translate(locale, 'toast.touched', { title: card.title }));
    } else {
      void useCards.getState().markDone(id);
      showToast(translate(locale, 'toast.done', { title: card.title }), { label: translate(locale, 'toast.undo'), run: () => void useCards.getState().restore(id) });
    }
    return true;
  };
  if (run()) return;
  const stop = useCards.subscribe(() => {
    if (run()) stop();
  });
}

/** Notifications posted by builds before the notifier still open their card, or the check-in. */
export async function installNotificationTaps(router: typeof Router): Promise<void> {
  const { LocalNotifications } = await import('@capacitor/local-notifications');
  await LocalNotifications.addListener('localNotificationActionPerformed', ({ actionId, notification }) => {
    const extra = (notification.extra ?? {}) as { cardId?: string; checkinTime?: string };
    if (actionId === 'done' && extra.cardId) {
      completeFromShade(extra.cardId);
      return;
    }
    void router.navigate(extra.checkinTime ? '/?checkin=1' : '/');
    if (extra.cardId) openCardAnywhere(extra.cardId);
  });
}
